import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2, GripVertical, Layers, ArrowUp, ArrowDown } from "lucide-react";
import { MEASUREMENT_LABELS } from "./constants";

export function LineItemsEditor({ lineItems = [], structures = [], onChange, priceList, showCashDiscount, onToggleCashDiscount, taxRate = 0, onTaxRateChange, allOptionPages = [], currentPageId, onMoveGroupToPage }) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [dragItemIdx, setDragItemIdx] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const items = lineItems || [];
  const groups = structures || [];

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ line_items: updated, structures: groups });
  };
  const removeItem = (idx) => {
    onChange({ line_items: items.filter((_, i) => i !== idx), structures: groups });
  };
  const addItem = (priceItem) => {
    const newItem = {
      id: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      price_list_id: priceItem.id,
      name: priceItem.name,
      unit_price: priceItem.base_price,
      margin: priceItem.margin || 0,
      quantity: 1,
      unit: priceItem.unit || "ea",
      visible: priceItem.default_visible !== false,
      measurement_key: priceItem.measurement_key || "",
      conversion_factor: priceItem.conversion_factor || 1,
      conversion_formula: priceItem.conversion_formula || "",
      waste_factor: priceItem.waste_factor || 0,
      structure_group: "",
    };
    onChange({ line_items: [...items, newItem], structures: groups });
    setShowPicker(false);
    setPickerSearch("");
  };
  const addStructure = () => {
    const newGroup = { id: `sg_${Date.now()}`, name: "New Group", shared: false };
    onChange({ line_items: items, structures: [...groups, newGroup] });
  };
  const updateStructure = (idx, field, value) => {
    const updated = [...groups];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ line_items: items, structures: updated });
  };
  const removeStructure = (idx) => {
    const groupId = groups[idx].id;
    const updatedItems = items.map(it => it.structure_group === groupId ? { ...it, structure_group: "" } : it);
    onChange({ line_items: updatedItems, structures: groups.filter((_, i) => i !== idx) });
  };
  const moveStructure = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= groups.length) return;
    const arr = [...groups];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange({ line_items: items, structures: arr });
  };

  // Native HTML5 drag-and-drop handlers for items
  const handleItemDragStart = (e, idx) => {
    setDragItemIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx.toString());
  };
  const handleItemDragEnd = () => { setDragItemIdx(null); setDropTarget(null); };
  const handleGroupDragOver = (e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(groupId);
  };
  const handleGroupDragLeave = (e, groupId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(prev => prev === groupId ? null : prev);
  };
  const handleGroupDrop = (e, groupId) => {
    e.preventDefault();
    if (dragItemIdx !== null) {
      updateItem(dragItemIdx, "structure_group", groupId);
    }
    setDragItemIdx(null);
    setDropTarget(null);
  };
  const handleUngroupedDrop = (e) => {
    e.preventDefault();
    if (dragItemIdx !== null) {
      updateItem(dragItemIdx, "structure_group", "");
    }
    setDragItemIdx(null);
    setDropTarget(null);
  };

  const calcItemTotal = (it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const margin = parseFloat(it.margin) || 0;
    const waste = parseFloat(it.waste_factor) || 0;
    const effectiveQty = qty * (1 + waste / 100);
    return effectiveQty * price * (1 + margin / 100);
  };
  const subtotal = items.reduce((sum, it) => sum + calcItemTotal(it), 0);
  const tax = subtotal * ((parseFloat(taxRate) || 0) / 100);
  const total = subtotal + tax;
  const cashPrice = total * 0.98;

  const filteredPriceList = (priceList || []).filter(p =>
    p.enabled !== false && (
      p.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
      p.category?.toLowerCase().includes(pickerSearch.toLowerCase())
    )
  );

  const ungroupedItems = items.filter(it => !it.structure_group);
  const renderItemRow = (it, idx) => {
    const itemTotal = calcItemTotal(it);
    const isDragging = dragItemIdx === idx;
    return (
      <div
        key={it.id}
        draggable
        onDragStart={e => handleItemDragStart(e, idx)}
        onDragEnd={handleItemDragEnd}
        className={`flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white group/li transition-all ${isDragging ? "opacity-40 scale-[0.98] bg-blue-50" : ""}`}
        data-testid={`line-item-${idx}`}
      >
        <div className="w-5 flex-shrink-0">
          <button type="button" className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"><GripVertical className="w-3.5 h-3.5" /></button>
        </div>
        <Switch checked={it.visible !== false} onCheckedChange={v => updateItem(idx, "visible", v)} className="scale-75" />
        <Input value={it.name} onChange={e => updateItem(idx, "name", e.target.value)} className="h-7 text-xs flex-1 min-w-0" />
        <Input type="number" value={it.quantity ?? ''} onChange={e => updateItem(idx, "quantity", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 text-xs w-16 text-center" />
        <span className="text-[10px] text-slate-400 w-8">{it.unit}</span>
        <div className="relative w-20"><span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span><Input type="number" step="0.01" value={it.unit_price ?? ''} onChange={e => updateItem(idx, "unit_price", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 text-xs pl-4" /></div>
        {it.margin > 0 && <span className="text-[9px] text-amber-600 font-medium w-10">+{it.margin}%</span>}
        <span className="text-xs font-semibold text-slate-700 w-20 text-right">${itemTotal.toFixed(2)}</span>
        {it.measurement_key && <Badge variant="outline" className="text-[8px] px-1 h-4 flex-shrink-0">{MEASUREMENT_LABELS[it.measurement_key] || it.measurement_key}{it.conversion_factor > 1 ? ` x${it.conversion_factor}` : ""}</Badge>}
        <Select value={it.structure_group || "none"} onValueChange={v => updateItem(idx, "structure_group", v === "none" ? "" : v)}>
          <SelectTrigger className="h-6 w-24 text-[9px] border-dashed"><SelectValue placeholder="Group" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No group</SelectItem>
            {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover/li:opacity-100" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
      </div>
    );
  };

  const otherPages = allOptionPages.filter(p => p.id !== currentPageId);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b">
        <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Line Items & Pricing</p>
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addStructure} data-testid="add-group-btn"><Layers className="w-3 h-3 mr-1" />Add Group</Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs bg-[#0F4C81] text-white hover:bg-[#0D4270] border-[#0F4C81]" onClick={() => setShowPicker(true)} data-testid="add-item-btn"><Plus className="w-3 h-3 mr-1" />Add Item</Button>
        </div>
      </div>

      <div className="p-2 space-y-1">
        <div className="flex items-center gap-2 px-2 py-1 text-[9px] uppercase font-bold text-slate-400 tracking-wider">
          <div className="w-5" /><div className="w-8">Vis</div><div className="flex-1">Name</div><div className="w-16 text-center">Qty</div><div className="w-8" /><div className="w-20 text-center">Price</div><div className="w-10" /><div className="w-20 text-right">Total</div><div className="w-12" /><div className="w-24">Group</div><div className="w-6" />
        </div>

        {groups.map((g, gi) => {
          const groupItems = items.map((it, idx) => ({ it, idx })).filter(({ it }) => it.structure_group === g.id);
          const isDropHover = dropTarget === g.id;
          const groupSubtotal = groupItems.reduce((sum, { it }) => sum + calcItemTotal(it), 0);
          return (
            <div
              key={g.id}
              className={`border-2 rounded-lg overflow-hidden mb-1 transition-all ${isDropHover ? "border-[#00A3E0] bg-[#00A3E0]/5 ring-2 ring-[#00A3E0]/20" : g.shared ? "border-blue-300 bg-blue-50/30" : "border-slate-200 bg-slate-50/50"}`}
              onDragOver={e => handleGroupDragOver(e, g.id)}
              onDragLeave={e => handleGroupDragLeave(e, g.id)}
              onDrop={e => handleGroupDrop(e, g.id)}
              data-testid={`structure-group-${gi}`}
            >
              <div className="flex items-center gap-2 px-2 py-1.5 bg-white/80 border-b border-slate-100">
                <div className="flex flex-col gap-0.5">
                  {gi > 0 && <button type="button" className="text-slate-300 hover:text-slate-600 h-3" onClick={() => moveStructure(gi, -1)}><ArrowUp className="w-3 h-3" /></button>}
                  {gi < groups.length - 1 && <button type="button" className="text-slate-300 hover:text-slate-600 h-3" onClick={() => moveStructure(gi, 1)}><ArrowDown className="w-3 h-3" /></button>}
                </div>
                <Layers className="w-3.5 h-3.5 text-slate-500" />
                <Input value={g.name} onChange={e => updateStructure(gi, "name", e.target.value)} className="h-6 text-xs font-semibold border-none bg-transparent px-1 flex-1" placeholder="Group name..." />
                {groupItems.length > 0 && <span className="text-[9px] text-slate-400 font-medium">${groupSubtotal.toFixed(2)}</span>}
                <label className="flex items-center gap-1 text-[9px] text-blue-600 font-medium whitespace-nowrap">
                  <input type="checkbox" checked={g.shared || false} onChange={e => updateStructure(gi, "shared", e.target.checked)} className="rounded w-3 h-3" />Shared
                </label>
                {otherPages.length > 0 && onMoveGroupToPage && (
                  <Select value="stay" onValueChange={v => { if (v !== "stay") onMoveGroupToPage(g.id, v); }}>
                    <SelectTrigger className="h-6 w-28 text-[9px] border-dashed bg-white" data-testid={`move-group-${gi}`}><SelectValue placeholder="Move to..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stay">Move to page...</SelectItem>
                      {otherPages.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.content?.option_name || p.content?.addon_name || p.title || "Untitled"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600" onClick={() => removeStructure(gi)}><Trash2 className="w-3 h-3" /></Button>
              </div>
              <div className={`px-1 min-h-[36px] transition-colors ${isDropHover ? "bg-[#00A3E0]/5" : ""}`}>
                {groupItems.length > 0 ? groupItems.map(({ it, idx }) => renderItemRow(it, idx)) : (
                  <p className="text-[10px] text-slate-400 italic px-2 py-3 text-center">{isDropHover ? "Drop item here" : "Drag items here or assign via dropdown"}</p>
                )}
              </div>
            </div>
          );
        })}

        <div
          className={`transition-colors rounded-lg ${dropTarget === "ungrouped" ? "bg-slate-100 ring-2 ring-slate-300/50" : ""} ${ungroupedItems.length > 0 || dragItemIdx !== null ? "min-h-[36px]" : ""}`}
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget("ungrouped"); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(prev => prev === "ungrouped" ? null : prev); }}
          onDrop={handleUngroupedDrop}
        >
          {ungroupedItems.length > 0 && (
            <div className="space-y-0">
              {groups.length > 0 && <p className="text-[9px] uppercase font-bold text-slate-300 tracking-wider px-2 pt-1">Ungrouped</p>}
              {items.map((it, idx) => !it.structure_group ? renderItemRow(it, idx) : null)}
            </div>
          )}
          {dragItemIdx !== null && ungroupedItems.length === 0 && groups.length > 0 && (
            <p className="text-[10px] text-slate-400 italic px-2 py-2 text-center">Drop here to ungroup</p>
          )}
        </div>

        {items.length === 0 && groups.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-6">No line items. Click "Add Item" to pull from your Price List.</p>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-1.5">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold text-slate-700">${subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-500 flex items-center gap-2">Tax
              <Input type="number" step="0.1" value={taxRate ?? ''} onChange={e => onTaxRateChange?.(e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-6 w-14 text-xs text-center inline-block" />
              <span className="text-[10px] text-slate-400">%</span>
            </span>
            <span className="font-medium text-slate-600">${tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-base font-bold border-t border-slate-300 pt-2"><span className="text-[#0F4C81]">Total</span><span className="text-[#0F4C81]">${total.toFixed(2)}</span></div>
          <div className="flex justify-between items-center text-sm pt-1">
            <label className="flex items-center gap-2 text-slate-500">
              <Switch checked={showCashDiscount !== false} onCheckedChange={v => onToggleCashDiscount?.(v)} className="scale-75" />
              Cash / Check Discount (2%)
            </label>
            {showCashDiscount !== false && <span className="font-semibold text-emerald-600">${cashPrice.toFixed(2)}</span>}
          </div>
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => { setShowPicker(false); setPickerSearch(""); }}>
          <div className="bg-white rounded-xl shadow-2xl w-[500px] max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h3 className="text-sm font-bold text-slate-800 mb-2">Add from Price List</h3>
              <Input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search items..." className="h-9" autoFocus data-testid="price-list-search" />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredPriceList.length > 0 ? filteredPriceList.map(p => (
                <button key={p.id} type="button" onClick={() => addItem(p)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors" data-testid={`pick-item-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{p.name}</p>
                    <p className="text-[10px] text-slate-400">{p.category} &middot; {p.unit || "ea"} {p.measurement_key ? `\u2022 auto: ${MEASUREMENT_LABELS[p.measurement_key] || p.measurement_key}` : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-emerald-600">${p.base_price?.toFixed(2)}</p>
                    {p.margin > 0 && <p className="text-[10px] text-amber-500">+{p.margin}% margin</p>}
                  </div>
                </button>
              )) : <p className="text-sm text-slate-400 text-center py-8">{(priceList || []).length === 0 ? "No price list items created yet. Go to Price List tab to add items." : "No matching items"}</p>}
            </div>
            <div className="p-3 border-t flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowPicker(false); setPickerSearch(""); }}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
